import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { AlertCircle, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { ComponentPropsWithoutRef } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Label } from '@core/components/Label';
import { Text } from '@core/components/Text';
import { cn } from '@core/utils/styles';

const selectVariants = cva(
  [
    // Layout
    'w-full flex items-center justify-between gap-2',

    // Background
    'bg-back-input-light',
    'dark:bg-back-input-dark',

    // Typography
    'text-txt-light',
    'dark:text-txt-dark',

    // Border
    'rounded-theme-min border',

    // Shadow
    'shadow-xs',

    // Interaction
    'focus:outline-hidden focus:ring',

    // Disabled
    'disabled:opacity-75',
  ],
  {
    variants: {
      variant: {
        default: [
          // Border
          'border-input-stroke-light/50',
          'dark:border-input-stroke-dark/50',

          // Ring, Outline
          'focus:border-primary-light',
          'focus:ring-primary-light',
          'dark:focus:ring-primary-light',

          // Interaction
          'active:border-input-stroke-light',
          'dark:active:border-input-stroke-dark',
          'group-hover:border-input-stroke-light',
          'dark:group-hover:border-input-stroke-dark',
        ],
        error: [
          // Typography
          'dark:text-txt-dark',

          // Border
          'border-red-500',
          'dark:border-red-500',

          // Ring, Outline
          'focus:border-red-500',
          'focus:ring-red-500',
          'dark:focus:ring-red-500',
        ],
      },
      size: {
        sm: 'px-2 py-1 text-sm',
        default: 'px-3 py-1.5 sm:min-h-9',
        lg: 'px-4 py-3 text-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  options: SelectOption[];
  error?: string;
  value?: string | string[];
  name?: string;
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  containerClassName?: string;
  onChange: (value: string | string[]) => void;
}

export const Select = ({
  options,
  error,
  value,
  label,
  hideLabel = false,
  disabled = false,
  onChange,
  name,
  multiple = false,
  placeholder,
  className,
  containerClassName,
  ...props
}: SelectProps) => {
  const { t } = useTranslation();
  const inputId = `input-${name}`;
  const selected =
    typeof value === 'string'
      ? options.find((option) => option.value === value)
      : options.filter((option) => value?.includes(option.value));

  const hasValue =
    (Array.isArray(selected) && selected.length > 0) || (!Array.isArray(selected) && !!selected);

  return (
    <div className={cn('group relative flex flex-col gap-1.5', containerClassName)} {...props}>
      <Label htmlFor={inputId} className={hideLabel ? 'sr-only' : ''}>
        {label}
      </Label>

      {error && (
        <div className="flex items-center gap-1">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <p id={`${inputId}-error`} className="text-sm text-red-500" role="alert">
            {error}
          </p>
        </div>
      )}

      <Listbox
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        multiple={multiple}
      >
        <ListboxButton
          className={cn(
            selectVariants({ variant: error ? 'error' : 'default', size: 'default' }),
            className
          )}
        >
          <Text
            size="sm"
            className={
              hasValue
                ? 'text-txt-light dark:text-txt-dark'
                : 'text-txt-light-muted dark:text-txt-dark-muted'
            }
          >
            {hasValue
              ? Array.isArray(selected)
                ? selected.map((option) => option.label).join(', ')
                : selected.label
              : (placeholder ?? t('common.select.placeholder'))}
          </Text>
          <ChevronDownIcon className="group pointer-events-none size-4" aria-hidden="true" />
        </ListboxButton>

        <ListboxOptions
          anchor="bottom"
          transition
          className={cn(
            'rounded-theme-max min-w-[var(--button-width)] border border-white/5 bg-white/5 p-1 backdrop-blur-xl [--anchor-gap:0.5rem] focus:outline-none',
            'transition duration-100 ease-in data-leave:data-closed:opacity-0',
            'border-stroke-light dark:border-stroke-dark border shadow-xs'
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              className="group rounded-theme data-focus:bg-back-dark/5 dark:data-focus:bg-back-light/10 flex cursor-default items-center gap-2 px-3 py-1.5 select-none"
            >
              <CheckIcon className="text-icon-light dark:text-icon-dark invisible size-4 group-data-selected:visible" />
              <Text size="sm">{option.label}</Text>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
};

interface FormSelectProps<T extends FieldValues>
  extends UseControllerProps<T>,
    Omit<SelectProps, 'name' | 'onChange' | 'defaultValue'> {
  options: SelectOption[];
  label: string;
}

/**
 * Select component implementation for react-hook-form.
 */
export const FormSelect = <T extends FieldValues>({
  name,
  control,
  rules,
  ...props
}: FormSelectProps<T>) => {
  const { field } = useController({ name, control, rules });

  return <Select {...field} {...props} />;
};
